import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
  computed,
} from '@angular/core'
import { Observable, map } from 'rxjs'

import {
  Enums,
  Environment,
  ExpandedProfile,
  Profile,
} from '@cyborg-monorepo/hunter/common'

import { CompanyService } from '../../../../shared/services/company.service'
import { EnvironmentService } from '../../../../shared/services/environment.service'
import {
  DashboardState,
  DashboardStateService,
} from '../dashboard-state-service'

@Component({
  standalone: false,
  selector: 'dashboard-filter', // Component selector used in the template
  templateUrl: './dashboard-filter.component.html',
  styleUrls: ['./dashboard-filter.component.scss'],
})
export class DashboardFilterComponent implements OnInit {
  environmentService = inject(EnvironmentService)
  companyService = inject(CompanyService)
  dashboardStateService = inject(DashboardStateService)
  private elementRef = inject(ElementRef)

  // Initial positions for the filter panel on the screen
  public topPosition = 90
  public rightPosition = 16

  // Array to store the environments fetched from the service
  public environments: Environment[]

  // Priority, status, and outcome options, derived from enums
  public priorities: Enums.PriorityEnum[] = Object.values(Enums.PriorityEnum)
  public statuses: Enums.StatusEnum[] = Object.values(Enums.StatusEnum)
  public outcomes: Enums.OutcomeEnum[] = Object.values(Enums.OutcomeEnum)

  // Observable for the list of users, combining profile and expanded profile data
  public users$: Observable<Array<Profile & ExpandedProfile>>
  public dashboardState$: Observable<DashboardState>

  // Boolean flag to show or hide the filter panel
  public showPanel = false

  // Status filter options with labels, values, and classes for styling
  public statusFilters: Array<{
    text: string
    value: Enums.StatusEnum
    class: string
  }> = [
    { text: 'To Do', value: Enums.StatusEnum.TO_DO, class: 'k-color-info' },
    {
      text: 'In Progress',
      value: Enums.StatusEnum.IN_PROGRESS,
      class: 'k-color-warning',
    },
    { text: 'Done', value: Enums.StatusEnum.DONE, class: 'k-color-success' },
  ]

  // Priority filter options with labels, values, and classes for styling
  public priorityFilters: Array<{
    text: string
    value: Enums.PriorityEnum
    class: string
  }> = [
    { text: 'Low', value: Enums.PriorityEnum.LOW, class: 'k-color-success' },
    {
      text: 'Medium',
      value: Enums.PriorityEnum.MEDIUM,
      class: 'k-color-warning',
    },
    { text: 'High', value: Enums.PriorityEnum.HIGH, class: 'k-color-error' },
  ]

  // Outcome filter options with labels, values, and classes for styling
  public outcomeFilters: Array<{
    text: string
    value: Enums.OutcomeEnum
    class: string
  }> = [
    {
      text: 'Detection',
      value: Enums.OutcomeEnum.DETECTION,
      class: 'k-color-warning',
    },
    {
      text: 'Escalated',
      value: Enums.OutcomeEnum.ESCALATED,
      class: 'k-color-error',
    },
    {
      text: 'No Findings',
      value: Enums.OutcomeEnum.NO_FINDINGS,
      class: 'k-color-success',
    },
    {
      text: 'Visibility Gap',
      value: Enums.OutcomeEnum.VISIBILITY_GAP,
      class: 'k-color-warning',
    },
  ]

  // Lifecycle hook that runs after component initialization
  public ngOnInit(): void {
    // Fetch all environments from the environment service and store them in the environments array
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })

    // Subscribe to the dashboard state once in the component
    this.dashboardState$ = this.dashboardStateService.dashboardState

    // Fetch users from the company service, and combine first and last name into fullName for display
    this.users$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            fullName: `${user.firstName} ${user.lastName}`, // Combine firstName and lastName
          }
        })
      }),
    )
  }

  // Method to reset all filters by calling the state service's reset method
  public resetFilters(): void {
    this.dashboardStateService.reset() // Resets the dashboard state to default
  }

  // Method to apply the filters and update the dashboard state, then close the filter panel
  public applyFilters(): void {
    this.dashboardStateService.updateState() // Applies the current filter settings
    this.showPanel = false // Hides the filter panel after applying
  }

  // Method to toggle the visibility of the filter panel
  public toggleFilterPanel(): void {
    this.showPanel = !this.showPanel // Toggles the showPanel flag to show/hide the filter panel
  }

  /**
   * Listens for document click and closes the filter panel if user clicks outside the filter panel
   * @param  event MouseEvent
   */
  @HostListener('document:click', ['$event']) // Host listener for document click events
  public onDocumentClick(event: MouseEvent): void {
    let clickedFilterButton = false // Flag to check if the click happened on the filter button

    let originalElement: HTMLElement | null = event.target as HTMLElement

    // Traverse up the DOM tree to check if the clicked element is the filter button
    while (originalElement) {
      if (originalElement.id === 'toggleFilters') {
        clickedFilterButton = true // If the filter button was clicked, set flag to true
        break
      }
      originalElement = originalElement.parentElement
    }

    // If the panel is open and the click was not on the filter button or inside the panel, close it
    if (
      this.showPanel &&
      !clickedFilterButton &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.toggleFilterPanel() // Close the filter panel
    }
  }
}
